import React, { useState, useMemo } from "react";
import Modal from "aldoo-ra/Modal";
import Typography from "aldoo-ra/Typography";
import Button from "aldoo-ra/Button";
import Input from "aldoo-ra/Input";
import Checkbox from "aldoo-ra/Checkbox";

/**
 * PluginDialog Component
 * Generic dialog for executing CMS plugins
 */
const PluginDialog = ({
  plugin,
  context,
  isOpen,
  onClose,
  onExecute
}) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isExecuting, setIsExecuting] = useState(false);

  // Get plugin interface
  const pluginInterface = useMemo(() => {
    if (!plugin || !context) {
      return null;
    }
    return plugin.getInterface(context);
  }, [plugin, context]);

  // Handle field change
  const handleFieldChange = (fieldId, value) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: value
    }));

    // Clear error for this field
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate required fields
    pluginInterface.dialog.fields.forEach(field => {
      // Check visibility
      if (field.visible && !field.visible(values)) {
        return;
      }

      // Check required (handle false as valid value)
      if (field.required && (values[field.id] === undefined || values[field.id] === null || values[field.id] === '')) {
        newErrors[field.id] = 'This field is required';
      }

      // Field-specific validation
      if (field.validate && values[field.id]) {
        const error = field.validate(values[field.id], values);
        if (error) {
          newErrors[field.id] = error;
        }
      }
    });

    // Form-level validation
    if (pluginInterface.dialog.validate) {
      const formValidation = pluginInterface.dialog.validate(values);
      if (!formValidation.valid && formValidation.errors) {
        formValidation.errors.forEach(error => {
          if (error.field) {
            newErrors[error.field] = error.message;
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle execute
  const handleExecute = async () => {
    if (!validateForm()) {
      return;
    }

    setIsExecuting(true);

    try {
      const result = await onExecute(values);

      if (result.success) {
        context.ui.showNotification(
          pluginInterface.summary.afterExecute(result),
          'success'
        );
        onClose();
      } else {
        context.ui.showNotification(
          `Failed: ${result.errors?.map(e => e.error).join(', ')}`,
          'error'
        );
      }
    } catch (error) {
      context.ui.showNotification(
        `Error: ${error.message}`,
        'error'
      );
    } finally {
      setIsExecuting(false);
    }
  };

  // Render field
  const renderField = (field) => {
    // Check visibility
    if (field.visible && !field.visible(values)) {
      return null;
    }

    const value = values[field.id] ?? field.defaultValue;
    const error = errors[field.id];

    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="mb-4">
            <Typography variant="admin_label" className="mb-1">
              {field.label}
              {field.required && <span className="text-admin_error ml-1">*</span>}
            </Typography>
            <Input
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              variant="admin"
            />
            {error && (
              <Typography variant="admin_caption" className="text-admin_error mt-1">
                {error}
              </Typography>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="mb-4">
            <Typography variant="admin_label" className="mb-1">
              {field.label}
              {field.required && <span className="text-admin_error ml-1">*</span>}
            </Typography>
            <select
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="w-full p-2 border border-admin_bordered rounded bg-admin_surface text-admin_text"
            >
              {field.placeholder && (
                <option value="">{field.placeholder}</option>
              )}
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <Typography variant="admin_caption" className="text-admin_error mt-1">
                {error}
              </Typography>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="mb-4 flex items-center">
            <Checkbox
              isOn={value}
              onChange={(checked) => handleFieldChange(field.id, checked)}
              className="w-full p-2 border border-admin_bordered rounded bg-admin_surface text-admin_text"
            />
            <Typography variant="admin_body" className="ml-2">
              {field.label}
            </Typography>
          </div>
        );

      case 'custom':
        if (!field.render) {
          return null;
        }
        return (
          <div key={field.id} className="mb-4">
            {field.render({
              value,
              values,
              onChange: (newValue) => handleFieldChange(field.id, newValue),
              error,
              field,
            })}
          </div>
        );

      default:
        return null;
    }
  };

  // Generate summary
  const summary = useMemo(() => {
    if (!pluginInterface?.summary?.beforeExecute || Object.keys(values).length === 0) {
      return null;
    }
    return pluginInterface.summary.beforeExecute(values, context);
  }, [pluginInterface, values, context]);

  if (!plugin || !pluginInterface) {
    return null;
  }

  const isHorizontalLayout = pluginInterface.dialog.layout === 'horizontal';
  const hideExecuteButton = pluginInterface.dialog.hideExecuteButton === true;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin"
      className={pluginInterface.dialog.width || pluginInterface.dialog.height
        ? "w-full h-full"
        : "w-full md:w-[90%] md:max-w-4xl h-full md:h-auto"
      }
      style={{
        ...(pluginInterface.dialog.width && { width: pluginInterface.dialog.width }),
        ...(pluginInterface.dialog.height && { height: pluginInterface.dialog.height })
      }}
    >
      <div className="p-6 h-full flex flex-col">
        {/* Header */}
        <div className="mb-4">
          <Typography variant="admin_h2" className="mb-2">
            {pluginInterface.dialog.title}
          </Typography>

          {plugin.description && (
            <Typography variant="admin_body" className="text-admin_muted">
              {plugin.description}
            </Typography>
          )}
        </div>

        {/* Content */}
        {isHorizontalLayout ? (
          <div className="flex gap-6 flex-1 overflow-hidden">
            {/* Left Side - Fields */}
            <div className="w-[500px] flex-shrink-0 overflow-y-auto">
              {pluginInterface.dialog.fields.map(renderField)}
            </div>

            {/* Right Side - Preview */}
            <div className="flex-1 overflow-y-auto border border-admin_bordered rounded p-4 bg-admin_surface">
              {pluginInterface.dialog.preview && (
                pluginInterface.dialog.preview.render(values, context)
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Fields */}
            <div className="mb-6">
              {pluginInterface.dialog.fields.map(renderField)}
            </div>

            {/* Preview */}
            {pluginInterface.dialog.preview && Object.keys(values).length > 0 && (
              <div className="mb-6 p-4 bg-admin_surface rounded border border-admin_bordered">
                <Typography variant="admin_label" className="mb-2">
                  Preview
                </Typography>
                {pluginInterface.dialog.preview.render(values, context)}
              </div>
            )}

            {/* Summary */}
            {summary && (
              <div className="mb-6 p-4 bg-admin_surface rounded border border-admin_bordered">
                <Typography variant="admin_label" className="mb-2">
                  Summary
                </Typography>

                <Typography variant="admin_body" className="mb-2">
                  {summary.itemsAffected} item{summary.itemsAffected !== 1 ? 's' : ''} will be affected
                </Typography>

                {summary.fieldsAffected && summary.fieldsAffected.length > 0 && (
                  <Typography variant="admin_body" className="mb-2">
                    Fields: {summary.fieldsAffected.join(', ')}
                  </Typography>
                )}

                {summary.changes && summary.changes.length > 0 && (
                  <div className="mb-2">
                    {summary.changes.map((change, index) => (
                      <div key={index} className="flex items-start mb-1">
                        <span className={`
                          text-xs px-2 py-0.5 rounded mr-2
                          ${change.impact === 'high' ? 'bg-admin_error text-white' : ''}
                          ${change.impact === 'medium' ? 'bg-admin_warning text-white' : ''}
                          ${change.impact === 'low' ? 'bg-admin_success text-white' : ''}
                        `}>
                          {change.impact}
                        </span>
                        <Typography variant="admin_caption">
                          {change.description}
                        </Typography>
                      </div>
                    ))}
                  </div>
                )}

                {summary.warnings && summary.warnings.length > 0 && (
                  <div className="mt-2">
                    {summary.warnings.map((warning, index) => (
                      <Typography key={index} variant="admin_caption" className="text-admin_warning">
                        ⚠ {warning}
                      </Typography>
                    ))}
                  </div>
                )}

                {summary.estimatedTime && (
                  <Typography variant="admin_caption" className="mt-2 text-admin_muted">
                    Estimated time: {summary.estimatedTime}
                  </Typography>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-admin_bordered mt-auto">
          <Button
            variant="admin_secondary"
            onClick={onClose}
            disabled={isExecuting}
          >
            Close
          </Button>
          {!hideExecuteButton && (
            <Button
              variant="admin_primary"
              onClick={handleExecute}
              disabled={isExecuting}
            >
              {isExecuting ? 'Executing...' : 'Execute'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PluginDialog;
